import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SubscriptionContext = createContext(null);

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within SubscriptionProvider');
  }
  return context;
};

export const SubscriptionProvider = ({ children }) => {
  const { user } = useAuth();
  const [subStatus, setSubStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStatus = useCallback(async () => {
    if (!user) {
      setSubStatus(null);
      setLoading(false);
      return;
    }
    try {
      const res = await axios.get(`${API_URL}/api/payments/subscription-status`);
      setSubStatus(res.data);
    } catch {
      setSubStatus(null);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus, user]);

  const canAddContent = subStatus?.can_add_content ?? true;
  const isExpired = subStatus && !subStatus.active && subStatus.type !== 'admin';
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';

  const contextValue = useMemo(() => ({
    subStatus,
    loading,
    canAddContent: isAdmin || canAddContent,
    isExpired: !isAdmin && isExpired,
    refresh: fetchStatus
  }), [subStatus, loading, isAdmin, canAddContent, isExpired, fetchStatus]);

  return (
    <SubscriptionContext.Provider value={contextValue}>
      {children}
    </SubscriptionContext.Provider>
  );
};
